import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { useLevelContentData } from "../../../utils/hooks/translateLevelContentDataHook";
import { transformLevelContent } from "../../../utils/transformLevelContent";
import { slugify } from "../../../utils/slugify";
import LectureProgressItem from "../LectureProgressItem.jsx";
import { courseRegistry } from "../../../courses/courseRegistry";
import useLevelSelection from "../../../utils/hooks/useLevelSelection.js";
import videoArrow from "../../../images/icons/videoArrow.svg";
import { useTranslation } from "../../../utils/hooks/translateHook";

const LecturesTree = ({ user, courseProgress }) => {
  const { language } = useLanguage();
  const t = useTranslation();
  const { courseSlug } = useParams();
  const courseConfig = courseRegistry[courseSlug];
  const rawLevelContentData = courseConfig?.levelContentData || [];
  const translatedLevelContentData = useLevelContentData(rawLevelContentData, courseSlug) || [];
  const {selectedLevel, mergedForSelected, userLevelContent} = useLevelSelection(user, translatedLevelContentData, courseSlug);
  
  const accessibleSubjects = useMemo(() => {
    return mergedForSelected?.subjects || [];
  }, [mergedForSelected]);

  const data = useMemo( () =>
     transformLevelContent(translatedLevelContentData, language) || [],
    [translatedLevelContentData, language]
  );

  const [openIndex, setOpenIndex] = useState(null);
  const [searchParams] = useSearchParams();
  const subjectFromUrl = searchParams.get("subject");

  useEffect(() => {
    if ( !subjectFromUrl || !accessibleSubjects.length || openIndex !== null) { return;}
    const index = accessibleSubjects.findIndex((s) => slugify(s.title) === subjectFromUrl);
    if (index !== -1) {setOpenIndex(index);}
  }, [subjectFromUrl, accessibleSubjects, openIndex]);

  const getLectureSlug = (lecture) => {
    const raw = typeof lecture === "string"
        ? lecture : lecture?.title;
    const str = typeof raw === "string"
     ? raw : typeof raw?.title === "string"
     ? raw.title : "";
    const match = str.match(/(\d+)[\.-](\d+)[\.-](\d+)/);
    if (!match) return "lecturesTree";
    return `${match[1]}-${match[2]}-${match[3]}`;
  };

  return (
    <div className="tree-back">
      <div className="tree-box">
        <div className="mobile-tree">
          <h4 className="title-sidebar"> 
            {t("lessons")} 
            <img src={videoArrow} alt="" className="video-sidebar-arrow"/>
          </h4>
          {accessibleSubjects.map((subject, idx) => (
            <div key={idx} className="mobile-subject">
              <div className="mobile-subject-title"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              >
                {subject.title.toUpperCase()}
              </div>

              {openIndex === idx && (
                <ul className="mobile-lectures">
                  {subject.lectures?.map((lecture, i) => {
                    const title = typeof lecture === "string"
                        ? lecture : typeof lecture.title === "string"
                        ? lecture.title : typeof lecture.title?.title === "string"
                        ? lecture.title.title : "";

                      const lectureSlug = getLectureSlug(lecture);
                      const isCompleted = courseProgress.some(
                        p =>
                          p.lessonSlug === lectureSlug &&
                          p.status === "completed"
                      );

                    return (
                      <LectureProgressItem
                        key={i}
                        title={title}
                        realTitle={title}
                        to={`/course/${courseSlug}/lectures/${getLectureSlug(lecture)}`}
                        allowToggle={false}
                        isCompleted={isCompleted}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LecturesTree;